import { TabItem, Sale, Tab } from '@/types';
import { itemOverridesTabDiscount } from '@/utils/tabTotals';
import { composeInvoiceDescription } from '@/lib/productModifiers';
import { createElectronicInvoice, getElectronicInvoiceStatus } from './haciendaService';
import type { ElectronicInvoiceItem, CreateElectronicInvoiceParams, CustomerInvoiceInfo, Discount, OtherCharge, HaciendaInvoiceStatusResponse } from './haciendaTypes';
import { 
  COLONES_PAYMENT_METHOD, 
  DOLLAR_PAYMENT_METHOD, 
  SINPE_MOVIL_PAYMENT_METHOD, 
  CREDIT_CARD_PAYMENT_METHOD,
  BANK_TRANSFER_PAYMENT_METHOD,
  EXTERNAL_ORDER_PAYMENT_METHOD,
} from '@/constants/defaultPaymentMethodContant';
import { isFeatureEnabled } from '@/lib/featureFlagsBridge';
import { FEATURE_FLAGS } from '@/types/featureFlags';
import { isWeightLine } from '@/utils/weightPricing';

export const HACIENDA_CASH_CODE = '01' as const; 
export const HACIENDA_CARD_CODE = '02' as const; 
export const HACIENDA_BANK_TRANSFER_CODE = '04' as const;
export const HACIENDA_SINPE_CODE = '06' as const; 
export const HACIENDA_NOT_SUPPORTED = 'NOT_SUPPORTED' as const;

export type HaciendaPaymentCode =
  | typeof HACIENDA_CASH_CODE
  | typeof HACIENDA_CARD_CODE
  | typeof HACIENDA_BANK_TRANSFER_CODE
  | typeof HACIENDA_SINPE_CODE
  | typeof HACIENDA_NOT_SUPPORTED;

export const HACIENDA_PAYMENT_CONDITION_CASH = '01';
//if use in the future
export const HACIENDA_PAYMENT_CONDITION_CREDIT = '02';

export const CUSTOMER_IDENTIFICATION_TYPE_PHYSICAL_ID = '01';
export const CUSTOMER_IDENTIFICATION_TYPE_LEGAL_ID = '02';
export const CUSTOMER_IDENTIFICATION_TYPE_DIMEX_ID = '03';

export const DOCUMENT_TYPE_ELECTRONIC_INVOICE= '01';
export const DOCUMENT_TYPE_CREDIT_NOTE = '03';
export const DOCUMENT_TYPE_ELECTRONIC_TICKET = '04';

//This is the document code for the anulation of an invoice
export const ANULATION_DOCUMENT_CODE = '01';

export const HAS_CREDIT_NOTE = "NC-";

export const INVOICE_STATUS_ACCEPTED = 'aceptada';
export const INVOICE_STATUS_REJECTED = 'rechazada';
export const INVOICE_STATUS_PENDING = 'pendiente';
export const INVOICE_STATUS_PROCESSING = 'procesando';
export const INVOICE_STATUS_CREDIT_NOTE = 'credit_note';

//Discount codes
//commercial discount
export const DISCOUNT_CODE = '07';
export const DISCOUNT_NATURE = 'comercial';

//Other charges
export const SERVICE_CHARGE_CODE = '06';
export const OTHER_CHARGE_CODE = '99';

const includeServiceCharge = (
  serviceCharge?: number,
  serviceChargeRate?: number
): boolean => {

  return (serviceCharge ?? 0) > 0 && (serviceChargeRate ?? 0) > 0;
};

const createOtherCharge = (serviceChargeRate: number): OtherCharge => ({
  document_type: SERVICE_CHARGE_CODE,
  detail: 'Servicio de mesa',
  percentage: serviceChargeRate,
});

const createOtherChargeTip = (amount: number): OtherCharge => ({
  document_type: OTHER_CHARGE_CODE,
  other_type_description: "Propina Voluntaria",
  detail: "Propina entregada por el cliente",
  amount,
});

const includeTip = (tip?: number): boolean => (tip ?? 0) > 0;

const buildOtherCharges = (
  serviceCharge?: number,
  serviceChargeRate?: number,
  tip?: number
): OtherCharge[] => {
  const charges: OtherCharge[] = [];
  if (includeServiceCharge(serviceCharge, serviceChargeRate) && serviceChargeRate !== undefined) {
    charges.push(createOtherCharge(serviceChargeRate));
  }
  if (includeTip(tip)) {
    charges.push(createOtherChargeTip(tip!));
  }
  return charges;
};


const createDiscount = (amount: number): Discount => ({
  discount_amount: amount.toFixed(2),
  discount_code: DISCOUNT_CODE,
  discount_nature: DISCOUNT_NATURE
});

export const isCreditNote = (invoiceId?: string | null): boolean => {
  if (!invoiceId) return false;
  return invoiceId.startsWith(HAS_CREDIT_NOTE);
};


export const formatCreditNoteId = (creditNoteId: string): string => {
  return `${HAS_CREDIT_NOTE}${creditNoteId}`;
};

export interface CustomerIdentificationType {
  id: string;
  label: string;
}
export interface CreateCreditNoteResult {
  success: boolean;
  creditNoteId?: string;
  error?: string;
}

export const CUSTOMER_IDENTIFICATION_TYPES: CustomerIdentificationType[] = [
  { id: CUSTOMER_IDENTIFICATION_TYPE_PHYSICAL_ID, label: 'Cédula Física' },
  { id: CUSTOMER_IDENTIFICATION_TYPE_LEGAL_ID, label: 'Cédula Jurídica' },
  { id: CUSTOMER_IDENTIFICATION_TYPE_DIMEX_ID, label: 'DIMEX' },
];

export const transformTabItemToElectronicItem = (
  tabItem: TabItem,
  generalDiscountPercentage?: number
): ElectronicInvoiceItem => {

  const taxPercentage = tabItem.product.productCategory?.cabysTax;  
  const tax = taxPercentage ? taxPercentage / 100 : 0; 
  
  const cabysCode = tabItem.product.productCategory?.cabysCode;
  
  // if the first digit is 5-9, it is a service, if it is 0-4, it is a product
  const firstDigit = cabysCode?.charAt(0) || 'no cabys code';
  const type: 'product' | 'service' = (firstDigit >= '5' && firstDigit <= '9') ? 'service' : 'product';
  
  const activityCode = tabItem.product.productCategory?.activityCode;
  
  const itemTotal = tabItem.unitPrice * tabItem.quantity;
  const applicablePercent = itemOverridesTabDiscount(tabItem)
    ? tabItem.discount
    : (generalDiscountPercentage ?? 0);
  const totalDiscountAmount =
    applicablePercent > 0 ? itemTotal * (applicablePercent / 100) : 0;
  
  const discount = totalDiscountAmount > 0 ? createDiscount(totalDiscountAmount) : undefined;

  const hasWeight = isWeightLine(tabItem);
  const haciendaQuantity = hasWeight ? tabItem.weightKg! : tabItem.quantity;
  const haciendaUnitPrice = hasWeight
    ? Math.round((tabItem.unitPrice / tabItem.weightKg!) * 100000) / 100000
    : tabItem.unitPrice;
  
  const item: ElectronicInvoiceItem = {
    codigo_cabys: cabysCode,
    quantity: haciendaQuantity,
    unit: hasWeight ? 'Kg' : 'Unid',
    description: composeInvoiceDescription(tabItem.product.name, tabItem.modifiers),
    unit_price: haciendaUnitPrice,
    tax,
    type,
    ...(activityCode && { activity_code: activityCode }),
    ...(discount && { discount })
  };
  return item;
};


export const createElectronicTicketParams = (
  tabItems: TabItem[],
  currency: string = 'CRC',
  paymentMethod: string = HACIENDA_CASH_CODE, 
  paymentCondition: string = HACIENDA_PAYMENT_CONDITION_CASH,
  tabDiscount?: number,
  serviceCharge?: number,
  serviceChargeRate?: number,
  tip?: number
): CreateElectronicInvoiceParams => {
  const items = tabItems.map(item => transformTabItemToElectronicItem(item, tabDiscount));
  
  const params: CreateElectronicInvoiceParams = {
    invoice_type: 'TE',
    payment_condition: paymentCondition,
    currency,
    items,
    medio_pago: paymentMethod,
  };

  const charges = buildOtherCharges(serviceCharge, serviceChargeRate, tip);
  if (charges.length > 0) {
    params.other_charges = charges;
  }
  return params;
};

export const getHaciendaPaymentCodeById = (defaultPaymentMethodId?: number): HaciendaPaymentCode => {
  switch (defaultPaymentMethodId) {

    case COLONES_PAYMENT_METHOD:
    case DOLLAR_PAYMENT_METHOD:
      return HACIENDA_CASH_CODE; 

    case CREDIT_CARD_PAYMENT_METHOD:
    case EXTERNAL_ORDER_PAYMENT_METHOD:
      return HACIENDA_CARD_CODE; 

    case BANK_TRANSFER_PAYMENT_METHOD:
      return HACIENDA_BANK_TRANSFER_CODE;

    case SINPE_MOVIL_PAYMENT_METHOD:
      return HACIENDA_SINPE_CODE; 

    default:
      return HACIENDA_NOT_SUPPORTED;
  }
};

export const isPaymentMethodSupportedByHacienda = (defaultPaymentMethodId?: number): boolean => {
  return getHaciendaPaymentCodeById(defaultPaymentMethodId) !== HACIENDA_NOT_SUPPORTED;
};

/** Local gates shared by POS close-tab and external-order completion (TE path). */
export const canIssueElectronicTicket = ({
  organizationSlug,
  digitalInvoicesEnabled,
  ivaDisabled,
  defaultPaymentMethodId,
}: {
  organizationSlug: string;
  digitalInvoicesEnabled?: boolean;
  ivaDisabled?: boolean;
  defaultPaymentMethodId?: number;
}): boolean => {
  return (
    isFeatureEnabled(organizationSlug, FEATURE_FLAGS.DIGITAL_INVOICES) &&
    !!digitalInvoicesEnabled &&
    !ivaDisabled &&
    isPaymentMethodSupportedByHacienda(defaultPaymentMethodId)
  );
};

const DEFAULT_INVOICE_ADDRESS = {
  province: '7',
  canton: '03',
  district: '06',
  other: 'Costa Rica',
} as const;

const DEFAULT_INVOICE_PHONE = {
  country_code: '506',
  number: '88888888',
} as const;

export type ExternalInvoiceMetadata = NonNullable<
  NonNullable<Tab['externalMetadata']>['invoice']
>;

export const buildCustomerInvoiceInfoFromExternalInvoice = (
  invoice: ExternalInvoiceMetadata,
): CustomerInvoiceInfo => ({
  name: invoice.name?.trim() ?? '',
  tipo: invoice.identificationType?.trim() || CUSTOMER_IDENTIFICATION_TYPE_PHYSICAL_ID,
  identification: invoice.identification?.trim() ?? '',
  activityCode: invoice.activityCode?.trim() ?? '',
  address: {
    province: invoice.address?.province?.trim() || DEFAULT_INVOICE_ADDRESS.province,
    canton: invoice.address?.canton?.trim() || DEFAULT_INVOICE_ADDRESS.canton,
    district: invoice.address?.district?.trim() || DEFAULT_INVOICE_ADDRESS.district,
    other: invoice.address?.other?.trim() || DEFAULT_INVOICE_ADDRESS.other,
  },
  phone: {
    country_code: invoice.phone?.countryCode?.trim() || DEFAULT_INVOICE_PHONE.country_code,
    number: invoice.phone?.number?.trim() || DEFAULT_INVOICE_PHONE.number,
  },
  email: invoice.email?.trim() ?? '',
});

export const getHaciendaPaymentCodeFromSale = (sale: Sale): HaciendaPaymentCode => {
  if (!sale.paymentMethods || sale.paymentMethods.length === 0) {
    return HACIENDA_NOT_SUPPORTED;
  }
  
  const primaryPaymentMethod = sale.paymentMethods[0];
  
  return getHaciendaPaymentCodeById(primaryPaymentMethod.paymentMethod.defaultPaymentMethodId);
};

export const createElectronicInvoiceParams = (
  sale: Sale,
  customerInfo: CustomerInvoiceInfo,
  currency: string = 'CRC',
  paymentCondition: string = HACIENDA_PAYMENT_CONDITION_CASH,
  isSimplifiedRegime?: boolean,
  forcedPaymentCode?: HaciendaPaymentCode,
): CreateElectronicInvoiceParams => {
  const items = sale.items.map(item => transformTabItemToElectronicItem(item, sale.discount));
  const mediosPago = forcedPaymentCode ?? getHaciendaPaymentCodeFromSale(sale);
  
  const params: CreateElectronicInvoiceParams = {
    invoice_type: 'FE',
    customer: {
      name: customerInfo.name,
      tipo: customerInfo.tipo,
      identification: customerInfo.identification,
      address: customerInfo.address,
      phone: {
        country_code: customerInfo.phone?.country_code?.trim() || DEFAULT_INVOICE_PHONE.country_code,
        number: customerInfo.phone?.number?.trim() || DEFAULT_INVOICE_PHONE.number,
      },
      email: customerInfo.email,
      ...(customerInfo.activityCode?.trim()
        ? { activityCode: customerInfo.activityCode.trim() }
        : {}),
    },
    payment_condition: paymentCondition,
    currency,
    items,
    medio_pago: mediosPago,
  };

  const charges = buildOtherCharges(sale.serviceCharge, sale.serviceChargeRate, sale.tip);
  if (charges.length > 0) {
    params.other_charges = charges;
  }

  if (isSimplifiedRegime !== undefined) {
    params.isSimplifiedRegime = isSimplifiedRegime;
  }
  return params;
};

export const createCreditNote = async (
  invoiceId: string
): Promise<CreateCreditNoteResult> => {
  try {
    const statusResponse = await getElectronicInvoiceStatus(invoiceId);
    const originalPayload = statusResponse.payload;

    if (!statusResponse.key) {
      throw new Error("missing-key");
    }
    
    const { key, created_at, type } = statusResponse;
    let documentType: string;

    switch (type) {
      case "FE":
        documentType = DOCUMENT_TYPE_ELECTRONIC_INVOICE;
        break;
      case "TE":
        documentType = DOCUMENT_TYPE_ELECTRONIC_TICKET;
        break;
      case "NC":
        documentType = DOCUMENT_TYPE_CREDIT_NOTE;
        break;
      default:
        throw new Error("invalid-type");
    }

    if (!key || !created_at || !documentType) {
      throw new Error("missing-reference-data");
    }

    const {
      invoice_type: _ignoredInvoiceType,
      reference_document: _ignoredReferenceDocument,
      ...restPayload
    } = originalPayload as Record<string, unknown>;

    const creditNotePayload = {
      ...restPayload,
      invoice_type: "NC",
      reference_document: {
        tipo: documentType,
        clave: key,
        fecha_emision: created_at,
        codigo: ANULATION_DOCUMENT_CODE,
        razon: "Anulación de la factura",
      },
    };
    
    const response = await createElectronicInvoice(
      creditNotePayload as CreateElectronicInvoiceParams
    );
    
    if (response.success) {
      return { 
        success: true, 
        creditNoteId: response.data?.invoice_id 
      };
    } else {
      return { 
        success: false, 
        error: response.error || "credit-note-error" 
      };
    }
  } catch (error) {
    console.warn("Error creating credit note:", error);
    const errorMessage = (error as Error)?.message || "unknown-error";
    return { success: false, error: errorMessage };
  }
};

function receptorNameFromInvoiceXml(xml: string): string | null {
  const match = xml.match(/<Receptor>[\s\S]*?<Nombre>([^<]+)<\/Nombre>/i);
  return match ? match[1].trim() : null;
}

export function getElectronicInvoicePrintFields(invoice: HaciendaInvoiceStatusResponse) {
  const key = invoice.key?.trim();
  if (!key) {
    throw new Error('missing-invoice-key');
  }

  const payload = invoice.payload as { customer?: { name?: string } } | undefined;
  const receptorNombre =
    payload?.customer?.name?.trim() ||
    (typeof invoice.xml === 'string' ? receptorNameFromInvoiceXml(invoice.xml) : null);

  if (!receptorNombre) {
    throw new Error('missing-receptor-name');
  }

  return { key, receptorNombre };
}